import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async'; // Added for SEO
import api from "../../api/axios"; 
import Header from '../Header/Header';
import Nav from '../Header/Nav';
import Footer from '../Footer/Footer';
import WhatssapIcon from '../Home/WhatssapIcon';

const ProductCard = ({ product }) => {
  const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL;

  const price = parseFloat(product.price);
  const oldPrice = product.old_price ? parseFloat(product.old_price) : null;
  const discount = (oldPrice && oldPrice > price) 
    ? Math.round(((oldPrice - price) / oldPrice) * 100) 
    : 0;

  const imageSrc = product.main_image.startsWith('http') 
    ? product.main_image 
    : `${BACKEND_BASE}${product.main_image}`;

  return (
    <motion.div 
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ y: -5 }}
      className="group relative flex flex-col bg-white p-4 transition-all duration-300 border border-gray-100 rounded-2xl shadow-sm hover:shadow-xl"
    >
      {discount > 0 && (
        <div className="absolute top-4 left-4 z-10 bg-[#FFAA00] px-3 py-1 text-[10px] font-black text-white uppercase tracking-widest rounded-lg shadow-sm">
          -{discount}%
        </div>
      )}

      <Link to={`/product/${product.slug}`} className="relative mb-4 h-48 w-full overflow-hidden sm:h-64">
        <img 
          src={imageSrc} 
          alt={`${product.brand} ${product.title}`} // Improved Alt Text for SEO
          className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-110"
          loading="lazy" // Performance optimization
        />
      </Link>

      <div className="flex flex-col text-center mt-auto">
        <span className="text-[10px] font-bold text-[#8A8A89] uppercase tracking-[0.2em] mb-1">
          {product.brand || 'Premium'}
        </span>
        <h3 className="text-sm font-bold text-gray-900 line-clamp-2 min-h-[40px] mb-3 uppercase tracking-tight group-hover:text-[#FFAA00] transition-colors">
          {product.title}
        </h3>
        
        <div className="flex flex-col items-center gap-1 mb-4">
          <span className="text-xl font-black text-[#FFAA00]">
            {product.price} <span className="text-xs">dh</span>
          </span>
          {product.old_price && (
            <span className="text-sm text-[#8A8A89] line-through font-bold">
              {product.old_price} dh
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const PromosPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState('asc'); 
  const [selectedBrand, setSelectedBrand] = useState('All');

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    
    const fetchAllPromos = async () => {
      try {
        const res = await api.get('/promotions'); 
        if (res.data.success) {
          setProducts(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching promotions:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAllPromos();
  }, []);

  const brands = useMemo(() => {
    const b = products.map(p => p.brand).filter(Boolean);
    return ['All', ...new Set(b)];
  }, [products]);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (selectedBrand !== 'All') {
      result = result.filter(p => p.brand === selectedBrand);
    }
    result.sort((a, b) => {
      return sortOrder === 'asc' 
        ? parseFloat(a.price) - parseFloat(b.price) 
        : parseFloat(b.price) - parseFloat(a.price);
    });
    return result;
  }, [products, selectedBrand, sortOrder]);

  return (
    <div className="bg-white min-h-screen">
      {/* --- SEO IMPLEMENTATION --- */}
      <Helmet>
        <title>Promotions & Offres Spéciales | Electro Titan Maroc</title>
        <meta 
          key="description" name="description" 
          content="Découvrez les meilleures promos sur l'électroménager et l'équipement électrique à Salé. Réductions exceptionnelles sur les frigos, machines à laver et plus chez Electro Titan." 
        />
        <meta name="keywords" content="promotions électroménager Maroc, soldes Electro Titan, réductions appareils électriques Salé, offres spéciales électroménager" />
        
        {/* Open Graph */}
        <meta key="og:title" property="og:title" content="Nos Promos | Electro Titan" />
        <meta key="og:description" property="og:description" content="Profitez de prix réduits sur une large sélection d'équipements électriques." />
        <meta key="og:type" property="og:type" content="website" />

        {/* CollectionPage Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Promotions Electro Titan",
            "description": "Liste des produits en promotion chez Electro Titan Maroc.",
            "url": window.location.href,
            "mainEntity": {
              "@type": "ItemList",
              "numberOfItems": filteredProducts.length,
              "itemListElement": filteredProducts.map((p, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "url": `https://www.titanelectro.ma/product/${p.slug}`
              }))
            }
          })}
        </script>
      </Helmet>

      <Header />
      <Nav target="promos" />

      <main className="w-full mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="relative mb-16 rounded-[2.5rem] overflow-hidden bg-[#8A8A89] p-8 md:p-16 text-white shadow-sm">
          <div className="relative z-10 max-w-2xl">
            <motion.span 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block px-4 py-1 rounded-full bg-[#FFAA00] text-white text-[10px] font-black uppercase tracking-[0.2em] mb-6"
            >
              Exclusivité Web
            </motion.span>
            <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-4 text-white">
              Nos <span style={{ color: '#FFAA00' }}>Promos</span>
            </h1>
            <p className="text-lg leading-7 text-gray-100 font-bold max-w-md leading-tight uppercase tracking-tight">
              Toutes les offres promotionnelles ELECTRO TITAN en un seul endroit.
            </p>
          </div>
          <div className="absolute right-[-5%] top-[-10%] text-[20rem] font-black text-white/15 pointer-events-none select-none italic">
            %
          </div>
        </div>

        {/* Filter Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-10 pb-6 border-b border-gray-100 gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-xs font-black uppercase tracking-widest text-gray-400">Trier par :</span>
            <div className="flex gap-2">
              <button 
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className={`px-4 py-2 border rounded-full text-[10px] font-bold uppercase transition-all ${sortOrder === 'asc' ? 'border-[#FFAA00] text-[#FFAA00]' : 'border-gray-200 text-gray-500'}`}
              >
                Prix {sortOrder === 'asc' ? 'Bas' : 'Haut'}
              </button>
              
              <select 
                onChange={(e) => setSelectedBrand(e.target.value)}
                value={selectedBrand}
                className="px-4 py-2 border border-gray-200 rounded-full text-[10px] font-bold uppercase outline-none hover:border-[#FFAA00] transition-all bg-white cursor-pointer text-gray-600"
              >
                {brands.map(brand => (
                  <option key={brand} value={brand}>{brand === 'All' ? 'Toutes les Marques' : brand}</option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-[10px] font-black text-[#8A8A89] uppercase tracking-widest">
            {filteredProducts.length} Articles Trouvés
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <Loader2 className="animate-spin text-[#FFAA00]" size={40} />
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Chargement des promos...</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
            <AnimatePresence mode="popLayout">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </AnimatePresence>

            {filteredProducts.length === 0 && (
              <div className="col-span-full text-center py-24 text-gray-400 font-bold uppercase tracking-widest border-2 border-dashed border-gray-100 rounded-3xl">
                Aucune promotion disponible pour le moment
              </div>
            )}
          </div>
        )}
      </main>

      <WhatssapIcon />
      <Footer />
    </div>
  );
};

export default PromosPage;